import { tokenDiagnostics, tokenValidator } from "@/utils"
import { MantineProvider } from "@mantine/core"
import "@mantine/core/styles.css"
import "@mantine/dates/styles.css"
import { ModalsProvider } from "@mantine/modals"
import { Notifications } from "@mantine/notifications"
import "@mantine/notifications/styles.css"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React, { Suspense, lazy } from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter } from "react-router-dom"
import App from "./App.tsx"
import "./index.css"
import { theme } from "./theme"

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

const showReactQueryDevtools = import.meta.env.VITE_SHOW_QUERY_DEVTOOLS === true
const ReactQueryDevtools = showReactQueryDevtools
  ? lazy(() => import("@tanstack/react-query-devtools").then((m) => ({ default: m.ReactQueryDevtools })))
  : null

// Migrate legacy localStorage tokens to sessionStorage (one-time, post-deploy cleanup).
// Users who were logged in before this change had tokens in localStorage. Move them to
// sessionStorage so they aren't unexpectedly logged out on first load after deploy.
// localStorage entries are removed immediately so the old behaviour cannot recur.
const legacyTokenKeys = ["access_token", "refresh_token", "user", "auth_token"]
legacyTokenKeys.forEach((key) => {
  const value = localStorage.getItem(key)
  if (value) {
    sessionStorage.setItem(key, value)
    localStorage.removeItem(key)
  }
})

// Enable token diagnostics in development mode
// Set VITE_ENABLE_TOKEN_DIAGNOSTICS=true in .env to enable
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_TOKEN_DIAGNOSTICS === "true") {
  console.log("🔍 Token diagnostics enabled. Use window.tokenDiagnostics in console.")
  tokenDiagnostics.enable()
}

// Start periodic token validation to catch expired refresh tokens early
// This prevents errors by redirecting to login before attempting failed refreshes
if (import.meta.env.PROD) {
  // Only in production - in dev we want to see the errors for debugging
  tokenValidator.startPeriodicValidation()
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-right" zIndex={1000} />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <QueryClientProvider client={queryClient}>
            <App />
            {ReactQueryDevtools && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </QueryClientProvider>
        </BrowserRouter>
      </ModalsProvider>
    </MantineProvider>
  </React.StrictMode>
)
